import React, { ReactElement } from 'react';
import {
  FastField,
  FastFieldProps,
  Field,
  FormikProps,
  FormikValues,
  useField,
  useFormikContext
} from 'formik';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import { GroupCreate, StudentLoginType } from 'types/schema-generated-types';
import { TFunction } from 'i18next';
import SecretCodeSelect from 'components/@common/SecretCodeSelect';
import { DisplayDirection } from 'components/@common/SecretCodeSelect/types';
import useDefaultGroupSettings from 'components/ClassroomsModule/hooks/use-default-group-settings';
import Select from 'components/@common/Inputs/Select';
import { gradeDropdownItems } from 'utilities/grade-util';

interface FieldProps {
  name: string;
  label?: string;
  className?: string;
  validate?: (values: object) => string;
  inputProps?: object;
  FormHelperTextProps?: object;
  SelectProps?: object;
  InputLabelProps?: object;
}

export interface StudentDetailsFieldsProps {
  classes: { [key: string]: string };
  name: string;
  t: TFunction;
  label?: string;
  defaultValue?: number | string;
  values?: string;
}

export const StudentGradeField: React.FC<StudentDetailsFieldsProps> = ({
  classes,
  name,
  t,
  label = 'Grade'
}) => (
  <FastField name={name}>
    {({ field, meta }: FastFieldProps): ReactElement => {
      const { value, ...remainingField } = field;
      return (
        <TextField
          select
          {...remainingField}
          placeholder={label}
          value={value || 'grade'}
          label={t(label)}
          variant="outlined"
          size="small"
          error={Boolean(meta.touched && meta.error)}
          helperText={meta.touched && meta.error}
          className={classes.input}
          inputProps={{
            'aria-label': t(label)
          }}
        >
          <MenuItem disabled value="grade">
            Grade
          </MenuItem>
          {gradeDropdownItems.map(x => (
            <MenuItem value={x.value}>{x.name}</MenuItem>
          ))}
        </TextField>
      );
    }}
  </FastField>
);

export const StudentTextField = ({
  classes,
  name,
  t,
  label = 'Field'
}: StudentDetailsFieldsProps): ReactElement => (
  <FastField name={name}>
    {({ field, meta }: FastFieldProps): ReactElement[] => [
      <TextField
        {...field}
        key={`${field.name}_input`}
        label={t(label)}
        fullWidth
        placeholder={t(label)}
        variant="outlined"
        size="small"
        error={Boolean(meta.touched && meta.error)}
        helperText={meta.touched && meta.error}
        className={classes.input}
        inputProps={{
          'aria-label': t(label)
        }}
      />
    ]}
  </FastField>
);

export const SecretCodeField = ({ name }: FieldProps): ReactElement => {
  const [secretCodeAnchorEl, setSecretCodeAnchorEl] = React.useState<null | HTMLElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, meta, helpers] = useField({ name });

  const { onChange, value } = field;

  const handleSecretCodeClose = React.useCallback((): void => {
    helpers.setTouched(true);
    setSecretCodeAnchorEl(null);
  }, [setSecretCodeAnchorEl, helpers]);

  const handleSecretChange = (loginType: StudentLoginType, code: string): void => {
    onChange({
      target: { value: { code, loginType }, name }
    });
  };

  return (
    <SecretCodeSelect
      displayDirection={DisplayDirection.COLUMN}
      open={secretCodeAnchorEl !== null}
      loginType={value.loginType}
      secretCode={value.code}
      error={meta.error}
      handleClose={handleSecretCodeClose}
      onSecretCodeChange={handleSecretChange}
      showUnselected={false}
    />
  );
};

export const StudentGroupField: React.FC<StudentDetailsFieldsProps> = ({
  classes,
  name,
  t,
  label = 'Group'
}) => {
  const { getDefaultGroupSettings } = useDefaultGroupSettings();
  const { setFieldValue, values }: FormikProps<FormikValues> = useFormikContext();

  const assignedProductCodes: [] = values?.licenses?.map((x: any) => x.productCode) ?? [];
  const defaultGroups = getDefaultGroupSettings(assignedProductCodes).map(x => x.group);

  const handleChange = (event: any): void => {
    setFieldValue(
      name,
      defaultGroups.find(group => group.name === event.target.value)
    );
  };

  return (
    <Field name={name}>
      {({ field, meta }: FastFieldProps): ReactElement => {
        const { value, ...remainingField } = field;
        return (
          <Select
            {...remainingField}
            disabled={defaultGroups.length === 0}
            className={classes.input}
            label={t(label)}
            placeholder={t(label)}
            value={value?.name}
            onChange={handleChange}
            error={Boolean(meta.touched && meta.error)}
            helperText={meta.touched && meta.error}
            inputProps={{
              'aria-label': t(label)
            }}
            size="small"
          >
            {defaultGroups.map((group: GroupCreate, idx: number) => (
              <MenuItem key={idx} value={group.name}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        );
      }}
    </Field>
  );
};
